'use client';

import { useContext } from 'react';

import { FormFieldContext } from '../form-field';
import { ComboboxRootContext } from './context/combobox-root.context';
import { useCombobox } from './hooks/use-combobox';
import type { ComboboxRootPropsInterface, ComboboxValueType } from './types/combobox-root.types';

/**
 * @since 8.28.0
 */
export const ComboboxRoot = <ValueType extends ComboboxValueType>(
  props: ComboboxRootPropsInterface<ValueType>,
) => {
  const formFieldContext = useContext(FormFieldContext);

  const {
    autoCompleteBehavior = 'automatic',
    appearance = 'select',
    children,
    disabled = formFieldContext.disabled || false,
    dividers = false,
    editable = true,
    error = formFieldContext.error || false,
    limitChipsShown,
    freeForm = false,
    loading = false,
    placeholder,
    required = formFieldContext.required || false,
    showOptionControl = false,
    size = 'md',
    showLabelForValue = false,
  } = props;

  const combobox = useCombobox(props);

  return (
    <ComboboxRootContext.Provider
      value={{
        autoCompleteBehavior,
        appearance,
        combobox,
        controlId: formFieldContext.controlId,
        descriptionId: formFieldContext.descriptionId,
        disabled,
        dividers,
        editable,
        error,
        freeForm,
        limitChipsShown,
        loading,
        placeholder,
        required,
        showOptionControl,
        size,
        showLabelForValue,
      }}
    >
      {children}
    </ComboboxRootContext.Provider>
  );
};

ComboboxRoot.displayName = 'Combobox.Root';
